import { useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import useCarCollection from '../../application/game/useCarCollection';
import useCarPrize from '../../application/game/useCarPrize';
import useDuelRun from '../../application/game/useDuelRun';
import useDuelSound from '../../application/game/useDuelSound';
import useLeaderboard from '../../application/game/useLeaderboard';
import { nextLockedCar, pointsShort, unlockTarget } from '../../domain/game/carCollection';
import AccountSheet from '../account/AccountSheet';
import CarPrizeReveal from '../game/CarPrizeReveal';
import DuelBoard from '../game/DuelBoard';
import DuelClock from '../game/DuelClock';
import DuelFoot from '../game/DuelFoot';
import DuelGauge from '../game/DuelGauge';
import DuelHero from '../game/DuelHero';
import DuelLadder from '../game/DuelLadder';
import DuelOption from '../game/DuelOption';
import DuelQuestion from '../game/DuelQuestion';
import DuelReveal from '../game/DuelReveal';
import DuelLeaderboard from '../game/DuelLeaderboard';
import DuelScoreboard from '../game/DuelScoreboard';
import { WheelIcon } from '../icons/ControlIcons';

/**
 * The duel (SPEC-007) — the schoolyard card game this catalogue is a deck for.
 *
 * One question about one figure, two cars, one tap, a clock that gets shorter
 * every round, and a run that lasts exactly as long as the reader keeps getting
 * it right. Everything about WHAT happens is `domain/game`; this screen is a
 * composition with no rule in it, in the same way `GarageScreen` is a
 * composition with no state in it.
 *
 * IT FITS ON ONE SCREEN AND DOES NOT SCROLL, which is the constraint the whole
 * layout is built around: a game where the answer is below the fold is a game
 * that asks the reader to scroll at the exact moment they most want to look at
 * something. So the screen is a fixed column — scoreboard, question, the pair,
 * then a RESULT REGION that takes all the slack. Empty while the question is
 * open, filled by the reveal after it, and the two cards above it never move
 * because the space the reveal needs was never theirs.
 *
 * The region keeps an `overflow-y` of its own as a safety valve rather than as
 * a plan: a landscape phone or a very large accessibility text size can defeat
 * any fixed layout, and content a reader cannot reach at all is a worse failure
 * than a short scroll inside one band.
 *
 * Every state here is a designed one, for the same reason the garage's are: this
 * is a black page, and a blank one is indistinguishable from a broken one.
 *
 * SINCE SPEC-010 THE POOL IS THE READER'S OWN CARS rather than the catalogue.
 * That is one word in the call below and it is the whole of the collection
 * reaching the game: a duel that asked about a car the reader has never been
 * shown would be punishing them for not having played it enough. The screen
 * still owns no rule — which cars are theirs is `domain/game/carCollection`, and
 * whether this run won one is `useCarPrize`.
 */

/** The question labels the two cards, so a group needs its id rather than a copy. */
const QUESTION_ID = 'duel-question';

export default function DuelScreen() {
    const t = useTranslate();
    const { cars, status } = useCarCatalogue();
    const { award, unlocked, wonIds } = useCarCollection({ cars });
    const {
        answer,
        board,
        claim,
        insight,
        next,
        rank,
        restart,
        run,
    } = useDuelRun({ cars: unlocked });
    // Watches the run for a rung it has cleared, mid-run or at its ending. The
    // car is put in the garage the instant it is owed and the ceremony opens
    // itself over whatever is on screen — a prize that only landed once an
    // animation had been watched is a prize a backgrounded tab could lose, and
    // one that has to be asked for is a prize the product is unsure about
    // (SPEC-010 PR-10.13).
    const { awarded, dismiss, prize } = useCarPrize({
        run, cars, wonIds, onAward: award, onClaim: claim,
    });
    // Both before the early returns below, because they are hooks. The shared
    // board does nothing at all for a signed-out reader and nothing until a run
    // ends, so calling it on a screen that is still loading costs a closure and
    // no request (SPEC-009 PR-9.4).
    const leaderboard = useLeaderboard({ run });
    // The clock, out loud, in its last five seconds — and the switch that stops
    // it. A hook rather than a call from the scoreboard because it owns two
    // things a component may not: a device preference, and an `AudioContext`
    // that has to be warmed inside a gesture (SPEC-007 D-52).
    const sound = useDuelSound({ run });
    const [isAccountOpen, setAccountOpen] = useState(false);

    // `run` is null for the first render, before the effect has opened one. That
    // is the same moment as the catalogue still being on its way, so it is the
    // same screen.
    if (status === 'loading' || !run) {
        return <DuelEmptyState message={t('duel.loading')} />;
    }

    if (status === 'failed') {
        return <DuelEmptyState message={t('duel.failed')} />;
    }

    // A catalogue too small or too broken to duel. It is fetched, so this is a
    // state the screen reaches in the wild rather than an impossible one.
    if (run.status === 'unavailable') {
        return <DuelEmptyState message={t('duel.unavailable')} />;
    }

    const revealed = run.status !== 'asking';
    const isOver = run.status === 'over';
    // What the NEXT car costs, measured from the record this run opened against
    // — which is why the run carries it rather than the screen reading storage.
    // Null once the ladder is spent or the garage is complete, and a null target
    // draws nothing at all rather than a zero.
    const target = nextLockedCar(cars, wonIds) ? unlockTarget(run.record, wonIds.length) : null;
    /**
     * How far this run still is from the next legend (SPEC-010 PR-10.8).
     *
     * Read every round rather than only at the end, because the gauge on the
     * scoreboard is what the near-miss line used to be and a reader chasing a
     * car wants to see it closing. `awarded` is not belt and braces: the moment
     * an ENDED run is handed its car the ladder moves up a rung underneath this
     * calculation, against a record that run has already beaten, and it would be
     * told it was short of the car it had just been given.
     *
     * A car COLLECTED mid-run is the opposite case and gets no such guard: the
     * bar moved with it, so the number under it is the honest price of the next
     * one — and quoting it immediately is the whole point of collecting early.
     */
    const short = target === null || awarded ? null : pointsShort({
        cars, wonIds, record: run.record, score: run.score,
    });
    const outcomeFor = (car) => {
        if (!revealed) {
            return null;
        }

        return car.id === run.verdict.winner.id ? 'winner' : 'loser';
    };

    return (
        <section className={`duel${revealed ? ' duel--revealed' : ''}${isOver ? ' duel--over' : ''}`}>
            {/* The composition has no room for a screen title — the question is
                what the reader is here for and it earns the top of the fold —
                but a screen still needs a heading to be announced and navigated
                by. Only the pixels are gone. */}
            <div className="visually-hidden">
                <h1>{t('duel.title')}</h1>
                <p>{t('duel.intro')}</p>
            </div>

            {/* Above everything, because it is the only band on this screen
                that is not about the round in front of the reader: it is the
                reason the run is being played (SPEC-010 D-25). */}
            <DuelLadder target={target} short={short} score={run.score} />

            <DuelScoreboard run={run} sound={sound} />

            {/* Keyed on the duel, so a new round re-enters rather than swapping
                its text under a reader who was mid-sentence. */}
            <DuelQuestion key={run.duel.key} spec={run.duel.spec} revealed={revealed} id={QUESTION_ID} />

            {/* THE ANSWER, IN THE SLOT THE QUESTION WAS ASKED IN (SPEC-007
                D-66). It was a plate under the pair, and paying for it collapsed
                the two cards to a letterbox every time a round closed — the
                reader answered on one layout and was shown the answer on
                another. Up here the two halves of a round are the same
                composition: what is being compared, the two figures, then the
                pair. The line it replaces is the instruction, which is furniture
                the moment there is an answer.

                A drawing, and `aria-hidden` for it: the reveal below is the live
                region, and it carries these figures as text. */}
            {revealed && insight ? <DuelGauge insight={insight} /> : null}

            {/* A group labelled by the question. "Ferrari F40, 1987" is not an
                answer to anything on its own, and a reader who lands on a card
                by tab has to arrive somewhere that says what is being asked. */}
            <div className="duel__options" role="group" aria-labelledby={QUESTION_ID}>
                {run.duel.cars.map((car, side) => (
                    <DuelOption
                        /* Keyed on the ROUND as well as the car, so the pair is
                           dealt fresh every time: a CSS entrance only plays for
                           an element that is new, and two consecutive rounds can
                           share a car. */
                        key={`${run.duel.key}:${car.id}`}
                        side={side}
                        car={car}
                        outcome={outcomeFor(car)}
                        isChosen={run.verdict?.chosen?.id === car.id}
                        onChoose={answer}
                    />
                ))}

                {/* The mark that says these two are against each other, slammed
                    down between them as they land. Decorative — the question
                    above already says what the pair is for, and a screen reader
                    reading "VS" between two buttons would be reading the
                    furniture. */}
                <span className="duel__versus" key={run.duel.key} aria-hidden="true">
                    {t('duel.versus')}
                </span>

                {/* The word the round is answered with, slammed over the two
                    cards. Inside the pair rather than over the whole column,
                    because centred on the column it landed on the primary
                    action — which is the one thing here nothing may cover.
                    Keyed on the ROUND so it replays on each one: a CSS
                    animation only runs for an element that is new. */}
                {revealed ? (
                    <DuelHero
                        key={run.duel.key}
                        outcome={isOver ? 'over' : 'correct'}
                        streak={run.streak}
                        points={run.points}
                        reaction={run.reaction}
                    />
                ) : null}
            </div>

            {/* Under the pair, which is where the eyes already are in the five
                seconds the clock is the point (SPEC-007 D-52). */}
            <DuelClock run={run} />

            {/* THE RESULT REGION, and on a wide screen it is the RIGHT-HAND
                RAIL rather than the slack under the pair — the boards are on
                screen beside the game instead of only after it (`duelRevealScale.css`).
                On a phone it is still the slack in the column, and the reason
                nothing above it moves when the answer opens. */}
            <div className="duel__result">
                {revealed && insight ? (
                    <DuelReveal verdict={run.verdict} insight={insight} run={run} />
                ) : null}

                {/* THE DEVICE'S BOARD, FOR THE WHOLE RUN. It was drawn inside
                    the reveal and only at an ending, which is where it belonged
                    while it was one line of scores under the cards on a phone.
                    Given a rail of its own it is what the reader is playing
                    AGAINST, so it is on screen while they play — and it draws
                    nothing at all until this browser has a run to show, which is
                    the same rule it has always followed.

                    The score is a ZERO while the run is live, and that is the
                    signal rather than a hedge: the standing sentence and the
                    final-score clause are both about a run that has FINISHED,
                    and "not a top five run this time" is a lie told to somebody
                    still playing. */}
                <DuelBoard board={board} rank={rank} score={isOver ? run.score : 0} />

                {/* Directly under the DEVICE board, and only once the run is
                    over. The two are different promises kept side by side
                    (SPEC-009): that one is the five best runs in this browser
                    and survives signing out, this one is everybody. A correct
                    answer is the middle of something and gets neither — the
                    reader is about to press Next, and `duelLeaderboard.spec.js`
                    holds the offer to an ending on every viewport, which is why
                    this one did NOT move into the rail full-time with the board
                    above it. */}
                {isOver ? (
                    <DuelLeaderboard board={leaderboard} onJoin={() => setAccountOpen(true)} />
                ) : null}
            </div>

            {/* Drawn under the clock by CSS `order`, and carrying the way to
                the duel once the run is over — both of which are `DuelFoot`'s
                own decisions to explain (SPEC-007 D-55, SPEC-008 PR-8.13). */}
            {revealed ? (
                <DuelFoot
                    isOver={isOver}
                    onAdvance={isOver ? restart : next}
                    pair={run.duel.cars}
                />
            ) : null}

            {/* Mounted only while it is open, so `showModal()` runs once per
                opening and the dialog carries nothing between them. */}
            {isAccountOpen ? <AccountSheet onClose={() => setAccountOpen(false)} /> : null}

            {/* Over everything, including the reveal band and the two boards
                under it — a car being won is not a thing to put beside a table
                of past scores. Keyed on the car so a second prize in the same
                RUN opens a fresh ceremony rather than re-dressing the one that
                is tearing down, which is now a thing that happens on a good
                afternoon rather than once a fortnight. The NEXT target is quoted
                from the record this run has just set, which is `run.score`: the
                run that won a car is by definition the new best.

                The way out of it says which run the reader is going back to. A
                run that has ended offers the competition; a run that is still
                live offers the streak it is still on, because that is what the
                reader is about to do and the ceremony should not make them
                wonder whether they have just spent it. */}
            {prize ? (
                <CarPrizeReveal
                    key={prize.id}
                    car={prize}
                    record={run.score}
                    nextTarget={
                        nextLockedCar(cars, wonIds)
                            ? unlockTarget(run.score, wonIds.length)
                            : null
                    }
                    closeKey={isOver ? 'prize.close' : 'prize.keepGoing'}
                    onClose={dismiss}
                />
            ) : null}
        </section>
    );
}

/**
 * The three states with no game in them.
 *
 * The same shape the garage uses for an empty shelf, because it is the same
 * situation: a screen that cannot show its content still has to feel like the
 * product rather than like a stack trace somebody caught.
 */
function DuelEmptyState({ message }) {
    return (
        <section className="duel duel--empty">
            <div className="empty-state">
                <WheelIcon className="empty-state__mark" size={32} />
                <p className="empty-state__message">{message}</p>
            </div>
        </section>
    );
}
